import React, { useEffect, useMemo, useState } from "react";

export const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 16, boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)" };
export const muted = { color: "#64748b", fontSize: 13 };
export const inputStyle = { width: "100%", minHeight: 44, border: "1px solid #cbd5e1", borderRadius: 12, padding: 12, fontSize: 14, fontFamily: "inherit" };
export const textareaStyle = { ...inputStyle, minHeight: 92, resize: "vertical" };
export const buttonStyle = { border: "1px solid #cbd5e1", borderRadius: 12, padding: "10px 14px", background: "#fff", cursor: "pointer", fontWeight: 700 };
export const primaryButtonStyle = { ...buttonStyle, background: "#0f172a", color: "#fff", borderColor: "#0f172a" };
export const chipStyle = { display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 999, padding: "6px 10px", fontSize: 12, fontWeight: 700 };
export const tableWrapStyle = { overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 14 };
export const thStyle = { textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb", background: "#f8fafc", fontSize: 12, color: "#475569", position: "sticky", top: 0 };
export const tdStyle = { padding: 10, borderBottom: "1px solid #f1f5f9", verticalAlign: "top", fontSize: 13, whiteSpace: "normal", wordBreak: "break-word" };

export const TAB_META = {
  tab1: { label: "Talk to Data", theme: "#1d4ed8" },
  tab2: { label: "Live Bid Intelligence", theme: "#0f766e" },
  tab3: { label: "Content Ideas", theme: "#9333ea" },
  tab4: { label: "Investigator", theme: "#dc2626" },
};

export const WELCOME_QUESTIONS = [
  "How many live GEM bids are there right now?",
  "Which ministry placed the most direct orders this year?",
  "Who are the top 10 sellers by contract value?",
  "Which categories have the highest single-bidder rate?",
  "Show me bid count trend month by month for 2025",
];

export function formatNumber(value) {
  if (value == null || value === "") return "-";
  if (typeof value === "number") return value.toLocaleString("en-IN");
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString("en-IN") : String(value);
}

export function detectEntityType(columnName) {
  const name = String(columnName || "");
  if (/bidnumber|bid_number|tenderid|bid_id/i.test(name)) return "bid";
  if (/seller|company_name|awardedbidder|l1seller/i.test(name)) return "seller";
  if (/ministry/i.test(name)) return "ministry";
  if (/department/i.test(name)) return "department";
  if (/office|office_zone/i.test(name)) return "office";
  if (/product_name|service_name|service_description/i.test(name)) return "product_service";
  if (/organisation|organization|buyer/i.test(name)) return "buyer";
  if (/category/i.test(name)) return "category";
  return null;
}

export function formatDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function todayDate() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

function normalizeDateBounds(ranges = []) {
  const values = [];
  for (const item of ranges) {
    if (item?.min) values.push(new Date(item.min));
    if (item?.max) values.push(new Date(item.max));
  }
  const valid = values.filter((date) => !Number.isNaN(date.getTime()));
  if (!valid.length) return { min: "", max: "" };
  valid.sort((a, b) => a.getTime() - b.getTime());
  const cappedMax = valid[valid.length - 1] > todayDate() ? todayDate() : valid[valid.length - 1];
  return {
    min: formatDateInput(valid[0]),
    max: formatDateInput(cappedMax),
  };
}

export function buildQuickDateRanges(bounds) {
  const max = bounds?.max ? new Date(bounds.max) : null;
  const min = bounds?.min ? new Date(bounds.min) : null;
  if (!max || !min || Number.isNaN(max.getTime()) || Number.isNaN(min.getTime())) return [];

  function clampDays(days) {
    const start = new Date(max);
    start.setDate(start.getDate() - days);
    if (start < min) return min;
    return start;
  }

  return [
    { key: "full", label: "Full available", from: bounds.min, to: bounds.max },
    { key: "30d", label: "Last 30 days", from: formatDateInput(clampDays(30)), to: bounds.max },
    { key: "90d", label: "Last 90 days", from: formatDateInput(clampDays(90)), to: bounds.max },
    { key: "365d", label: "Last 365 days", from: formatDateInput(clampDays(365)), to: bounds.max },
  ];
}

export function DateRangeControls({ bounds, dateFrom, dateTo, onChange }) {
  const quickRanges = useMemo(() => buildQuickDateRanges(bounds), [bounds]);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gap: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>From</label>
        <input type="date" style={inputStyle} min={bounds?.min || ""} max={bounds?.max || ""} value={dateFrom} onChange={(e) => onChange({ dateFrom: e.target.value, dateTo })} />
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>To</label>
        <input type="date" style={inputStyle} min={bounds?.min || ""} max={bounds?.max || ""} value={dateTo} onChange={(e) => onChange({ dateFrom, dateTo: e.target.value })} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {quickRanges.map((item) => (
          <button key={item.key} style={{ ...buttonStyle, fontSize: 12, padding: "8px 10px" }} onClick={() => onChange({ dateFrom: item.from, dateTo: item.to })}>
            {item.label}
          </button>
        ))}
      </div>
      <div style={muted}>Live coverage: {bounds?.min || "-"} to {bounds?.max || "-"}</div>
    </div>
  );
}

export function useElapsed(active) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return undefined;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 250);
    return () => clearInterval(timer);
  }, [active]);
  return elapsed;
}

export function ResultsTable({ rows, onEntityClick }) {
  const columns = useMemo(() => Object.keys(rows?.[0] || {}), [rows]);
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const sortedRows = useMemo(() => {
    const base = Array.isArray(rows) ? [...rows] : [];
    if (!sortKey) return base;
    base.sort((a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const an = Number(av);
      const bn = Number(bv);
      if (Number.isFinite(an) && Number.isFinite(bn)) return sortDir === "asc" ? an - bn : bn - an;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return base;
  }, [rows, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  if (!rows?.length) return <div style={{ ...muted, padding: 12 }}>No rows.</div>;

  return (
    <div style={tableWrapStyle}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} style={{ ...thStyle, cursor: "pointer" }} onClick={() => toggleSort(column)}>
                {column}{sortKey === column ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕"}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {columns.map((column) => {
                const entityType = detectEntityType(column);
                const value = row[column];
                if (entityType && value != null && value !== "") {
                  return (
                    <td key={column} style={tdStyle}>
                      <button style={{ border: 0, padding: 0, background: "transparent", color: "#2563eb", cursor: "pointer", textAlign: "left", font: "inherit" }} onClick={() => onEntityClick?.(entityType, String(value))}>
                        {String(value)}
                      </button>
                    </td>
                  );
                }
                return <td key={column} style={tdStyle}>{typeof value === "number" ? formatNumber(value) : String(value ?? "-")}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TabButton({ id, currentTab, onClick }) {
  const active = currentTab === id;
  const meta = TAB_META[id];
  return (
    <button onClick={() => onClick(id)} style={{ ...buttonStyle, background: active ? meta.theme : "#fff", color: active ? "#fff" : "#0f172a", borderColor: active ? meta.theme : "#cbd5e1" }}>
      {meta.label}
    </button>
  );
}

export function EntityDrawer({ profile, loading, error, onClose, onEntityClick }) {
  if (!profile && !loading && !error) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.28)", zIndex: 1100, display: "flex", justifyContent: "flex-end" }}>
      <div style={{ width: "min(94vw, 520px)", height: "100%", background: "#fff", boxShadow: "-20px 0 40px rgba(15,23,42,0.18)", padding: 18, overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Entity Profile</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{profile ? `${profile.entity_type}: ${profile.entity_id}` : "Loading..."}</div>
          </div>
          <button style={buttonStyle} onClick={onClose}>Close</button>
        </div>
        {loading ? <div style={muted}>Loading entity profile...</div> : null}
        {error ? <div style={{ color: "#b91c1c", fontWeight: 700 }}>{error}</div> : null}
        {profile ? (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ ...cardStyle, padding: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>AI Summary</div>
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{profile.summary || "No summary available."}</div>
            </div>
            {Object.entries(profile.sections || {}).map(([key, value]) => (
              <div key={key} style={{ ...cardStyle, padding: 14 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>{key.replace(/_/g, " ")}</div>
                <ResultsTable rows={value.rows || []} onEntityClick={onEntityClick} />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function parseMessageRawData(message) {
  if (!message?.raw_data) return null;
  if (typeof message.raw_data === "object") return message.raw_data;
  try {
    return JSON.parse(message.raw_data);
  } catch {
    return null;
  }
}

export function latestAssistantPayload(messages) {
  const assistant = [...(messages || [])].reverse().find((message) => message.role === "assistant");
  return assistant ? { message: assistant, raw: parseMessageRawData(assistant) } : { message: null, raw: null };
}

export function useSchemaOptions(schema) {
  return useMemo(() => {
    const out = {
      ministries: [],
      states: [],
      categories: [],
      bidTypes: [],
      buyerTypes: [],
      sellerTypes: [],
      dateBounds: {
        global: { min: "", max: "" },
        contracts: { min: "", max: "" },
        cleanedContracts: { min: "", max: "" },
        gemBids: { min: "", max: "" },
        cpppTenders: { min: "", max: "" },
      },
    };
    if (!schema?.prompt_tables) return out;

    function addFrom(tableName, columnName, key) {
      const table = schema.prompt_tables.find((item) => item.table === tableName || item.full_table_id?.endsWith(tableName));
      const values = table?.sample_values?.[columnName];
      if (Array.isArray(values)) {
        values.forEach((row) => {
          const value = String(row.value || "").trim();
          if (value && !out[key].includes(value)) out[key].push(value);
        });
      }
    }

    addFrom("tm_gem_v2.gem_bids", "Ministry", "ministries");
    addFrom("tm_gem_v2.gem_bids", "State", "states");
    addFrom("tm_gem_v2.gem_bids", "CategoryTag", "categories");
    addFrom("tm_gem_v2.gem_bids", "BidType", "bidTypes");
    addFrom("tm_dashboard.contracts_dashboard_tbl", "organization_type", "buyerTypes");
    addFrom("tm_gem_v2.gem_cleaned_contracts_v3", "msme_verified", "sellerTypes");
    addFrom("tm_gem_v2.gem_cleaned_contracts_v3", "selling_as", "sellerTypes");

    const allRanges = (schema.tables || []).flatMap((table) => table.date_ranges || []);
    out.dateBounds.global = normalizeDateBounds(allRanges);

    function tableBounds(tableName) {
      const table = (schema.tables || []).find((item) => item.full_table_id === tableName || item.full_table_id?.endsWith(tableName));
      return normalizeDateBounds(table?.date_ranges || []);
    }

    out.dateBounds.contracts = tableBounds("tm_dashboard.contracts_dashboard_tbl");
    out.dateBounds.cleanedContracts = tableBounds("tm_gem_v2.gem_cleaned_contracts_v3");
    out.dateBounds.gemBids = tableBounds("tm_gem_v2.gem_bids");
    out.dateBounds.cpppTenders = tableBounds("tm_gem_v2.cppp_tenders");
    return out;
  }, [schema]);
}
